import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import io from 'socket.io-client';
import { FaClock } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const socket = io('https://quizbk.shirazkhan.me/');

const QuizPage = () => {
  const { roomCode } = useParams();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answer, setAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [playerName, setPlayerName] = useState(localStorage.getItem('playerName'));
  const [isHost, setIsHost] = useState(false);

  useEffect(() => {
    socket.emit('join-room', roomCode);

    // Check if the current user is the host
    setIsHost(localStorage.getItem('hostId') === roomCode);

    socket.on('game-started', (data) => {
      setQuestions(data.questions);
      setGameStarted(true);
      setLeaderboard(data.leaderboard);
      setTimeLeft(data.timeLimit);
    });

    let timer = null;
    if (!isHost && timeLeft > 0) { // Host doesn't need timer
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            socket.emit('time-up', { roomCode });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    socket.on('leaderboard-updated', (data) => {
      setLeaderboard(data);
    });

    socket.on('game-ended', (finalLeaderboard) => {
      setGameEnded(true);
      setLeaderboard(finalLeaderboard);
      clearInterval(timer);
    });

    // Listen for user-joined event
    socket.on('user-joined', ({ playerName }) => {
      toast.info(`${playerName} has joined the room!`, {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    });

    return () => {
      socket.off('game-started');
      socket.off('leaderboard-updated');
      socket.off('game-ended');
      socket.off('user-joined');
      clearInterval(timer);
    };
  }, [roomCode, timeLeft]);

  const submitAnswer = async () => {
    const response = await fetch('https://quizbk.shirazkhan.me/submit-answer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        roomCode,
        playerId: localStorage.getItem('playerId'),
        questionIndex: currentQuestion,
        answer
      })
    });

    const data = await response.json();
    setScore(data.score);

    // Trigger real-time leaderboard update after answer submission
    socket.emit('request-leaderboard-update', { roomCode });

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setGameEnded(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 to-purple-500 flex flex-col items-center justify-center p-4">
      <ToastContainer />
      <h1 className="text-4xl font-bold text-white mb-6">Quiz Game</h1>

      {/* Timer Display (Hide for host) */}
      {!isHost && !gameEnded && (
        <div className="absolute top-4 left-4 flex items-center text-white text-2xl font-bold">
          <FaClock className="mr-2 text-3xl" />
          {timeLeft}s
        </div>
      )}

      {/* Small Leaderboard (Hide for host) */}
      {!isHost && !gameEnded && (
        <div className="lg:block lg:absolute lg:top-4 lg:right-4 lg:w-64 bg-white rounded-lg shadow-lg p-4">
          <h2 className="text-lg font-bold mb-4">Leaderboard</h2>
          <ul>
            {leaderboard.map((player, index) => (
              <li
                key={index}
                className={`flex justify-between p-3 border-b text-sm font-medium mb-2 ${
                  player.name === playerName ? 'border-b-4 border-solid border-4 border-cyan-500' : ''
                } ${
                  index === 0 ? 'bg-yellow-300' : index === 1 ? 'bg-gray-300' : index === 2 ? 'bg-orange-300' : 'bg-white'
                }`}
              >
                <span>{index + 1}{index === 0 ? 'st' : index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'}</span>
                <span>{player.name === playerName ? 'You' : player.name}</span>
                <span>{player.score}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {gameEnded || isHost ? (
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-center">Leaderboard</h2>
          <ul>
            {leaderboard.map((player, index) => (
              <li
                key={index}
                className={`flex justify-between p-3 border-b text-lg font-medium mb-2 ${
                  player.name === playerName ? 'border-b-4 border-solid border-4 border-cyan-500' : ''
                } ${
                  index === 0 ? 'bg-yellow-300' : index === 1 ? 'bg-gray-300' : index === 2 ? 'bg-orange-300' : 'bg-white'
                }`}
              >
                <span>{index + 1}{index === 0 ? 'st' : index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'}</span>
                <span>{player.name}</span>
                <span>{player.score}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : gameStarted ? (
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4">
            {questions[currentQuestion]?.question}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {questions[currentQuestion]?.options.map((option, index) => (
              <button
                key={index}
                onClick={() => setAnswer(option)}
                className={`p-4 rounded-lg text-lg font-medium shadow-md transition-all duration-200 ${answer === option ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                {option}
              </button>
            ))}
          </div>
          <button
            onClick={submitAnswer}
            className="mt-6 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg shadow-lg"
          >
            Submit Answer
          </button>
        </div>
      ) : (
        <p className="text-xl text-white">Waiting for the game to start...</p>
      )}
    </div>
  );
};

export default QuizPage;
